import {
    Alert,
    Group,
    Stack,
    Text,
} from '@mantine/core';
import {AiOutlineInfoCircle} from 'react-icons/ai';
import {
    PlatformPropertyCard,
    PlatformPropertyCardEntryInput,
} from '@flexinet/ui-components';

import {
    TestRun,
    TestRunStatus,
} from '../types';
import {TestRunStatusBadge} from './TestRunStatusBadge.tsx';

// READ-ONLY (FNP-969). No SetStatusButtons, no edit affordance — a run's status
// is written by the executor closing it or by the abandon sweep, never by a
// click.
export const Profile = ({record}: { record: TestRun }) => {

    const isAbandoned = record.status === TestRunStatus.ABANDONED;
    const isRunning = record.status === TestRunStatus.RUNNING;

    const identity: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Test Code',
            // Falls back to the raw definition id: testCode is a nullable
            // SNAPSHOT taken at open time, so a run can legitimately lack it.
            value: record.testCode ?? 'n/a',
            formatter: 'code',
            span: 12,
        }, {
            label: 'Test Definition',
            value: record.testDefinitionId,
            formatter: 'code',
            span: 12,
        }, {
            label: 'Synthetic User',
            value: record.syntheticUserId,
            formatter: 'code',
            span: 12,
        }, {
            label: 'Persona',
            value: record.personaId ?? 'n/a',
            formatter: 'code',
            span: 12,
        }];

    // The full lifecycle, in the order it happens.
    const lifecycle: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Started',
            value: record.startedAt,
            formatter: 'timestamp',
            span: 12,
        },
        // NULL while in flight. Stated in words rather than left blank, so
        // "still running" is asserted rather than inferred from an empty row.
        // The formatter is chosen by branching on the WHOLE entry rather than on
        // the formatter field: PlatformPropertyCardEntryInput is a discriminated
        // union, so a conditional `formatter` does not narrow the `value` type.
        record.completedAt
        ? {
                label: 'Completed',
                value: record.completedAt,
                formatter: 'timestamp' as const,
                span: 12,
            }
        : {
                label: 'Completed',
                value: isRunning ? 'in flight' : 'n/a',
                span: 12,
            },
        {
            // ⚠️ THE KEY FIELD FOR AN ABANDONED RUN (FNP-969): which canary run
            // held the lease. For an abandoned run this is the ONLY evidence of
            // which canary stranded it — there is nothing else to go on.
            label: 'Claimed By',
            value: record.claimedBy ?? 'n/a',
            formatter: 'code',
            span: 12,
        },
        // The API REMOVES this when the run closes, so a value on a closed run is
        // not meaningful. Only shown as a live deadline while the run is open.
        record.leaseExpiresAt
        ? {
                label: 'Lease Expires',
                value: record.leaseExpiresAt,
                formatter: 'timestamp' as const,
                span: 12,
            }
        : {
                label: 'Lease Expires',
                value: 'n/a',
                span: 12,
            }];

    const steps: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Steps Total',
            value: record.stepsTotal ?? 'not recorded',
            span: 12,
        }, {
            label: 'Steps Passed',
            value: record.stepsPassed ?? 'not recorded',
            span: 12,
        }, {
            label: 'Steps Failed',
            value: record.stepsFailed ?? 'not recorded',
            span: 12,
        }, {
            label: 'Steps Skipped',
            value: record.stepsSkipped ?? 'not recorded',
            span: 12,
        }];

    const audit: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Created',
            value: record.created,
            formatter: 'timestamp',
            span: 12,
        }, {
            label: 'Last Updated',
            value: record.updated,
            formatter: 'timestamp',
            span: 12,
        }];

    return (
        <Stack gap={'xl'}>
            <Group justify={'space-between'}>
                <Text size={'sm'} c={'dimmed'}>Status</Text>
                <TestRunStatusBadge status={record.status}/>
            </Group>

            {/*
              Stated on the page, not just in the badge tooltip. An operator
              landing on an ABANDONED run is deciding whether to go hunting a
              product bug — the answer is no, and it must not depend on hovering.
            */}
            {isAbandoned && (
                <Alert
                    icon={<AiOutlineInfoCircle/>}
                    color='gray'
                    title='Abandoned is not a failure'
                >
                    This run was closed out by the abandon sweep because its lease expired —
                    the canary died mid-wake. It says nothing about whether the product under
                    test is working. The step counts below are whatever had been reported
                    before the canary stopped, not a complete result. Check
                    <Text span fw={600}> Claimed By </Text>
                    to identify which canary run stranded it.
                </Alert>
            )}

            <PlatformPropertyCard properties={identity}/>

            <Stack gap={'xs'}>
                <Text size={'sm'} c={'dimmed'}>Lifecycle</Text>
                <PlatformPropertyCard properties={lifecycle}/>
            </Stack>

            <Stack gap={'xs'}>
                <Text size={'sm'} c={'dimmed'}>Steps</Text>
                <PlatformPropertyCard properties={steps}/>
            </Stack>

            <PlatformPropertyCard properties={audit}/>
        </Stack>
    );
};
